import type { ChangeEvent } from "react";
import type { FilterFieldDefinition } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import type { ValidityPeriodType } from "@features/port-security/components/SecurityClearence.types.ts";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema.ts";
import type { Nationality } from "@services/gatepass/types";
import type {
  Document,
  MilitaryService,
  Relative,
  SecurityClearanceDetails,
  SecurityClearanceLatestReasonEvent,
  SecurityClearanceLatestReasonSource,
  SecurityClearanceTaskHistoryEntry,
} from "@services/security-clearance/types.ts";
import { paths } from "@utils/navigation";
import { SECURITY_OPERATOR_ROLE, SECURITY_SUPERVISOR_ROLE } from "@utils/user-permissions";
import { type TFunction } from "i18next";
import type { UseFormClearErrors, UseFormSetError } from "react-hook-form";

const { portSecurity } = paths;

export const NO_PASSPORT_KEY = "security-clearance-no-passport";

export const DATE_ONLY_FIELDS = new Set<keyof PortSecurityFormData>([
  "dateOfBirth",
  "dateOfEntry",
  "passportExpiryDate",
  "passportDateOfIssue",
  "emiratesIdExpiryDate",
  "emiratesIdDateOfIssue",
  "visaDateOfIssue",
  "visaExpiryDate",
  "certificateDate",
  "spouseBirthDate",
  "fatherBirthDate",
  "motherBirthDate",
  "dateOfIssue",
  "dateOfLicenseExpiry",
  "militaryServiceStartDate",
  "militaryServiceEndDate",
  "signatureDate",
]);

export const toDateOnlyString = (value: Date | string | null | undefined): string => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const SECURITY_CLEARANCE_STATUS = {
  PENDING_SPONSOR_COMPANY_APPROVAL: "PENDING_SPONSOR_COMPANY_APPROVAL",
  PENDING_SECURITY_DEPT_APPROVAL: "PENDING_SECURITY_DEPT_APPROVAL",
  PENDING_SECURITY_SUPERVISOR_APPROVAL: "PENDING_SECURITY_SUPERVISOR_APPROVAL",
  PENDING_RESUBMISSION: "PENDING_RESUBMISSION",
  APPROVED: "APPROVED",
  SUSPENDED: "SUSPENDED",
  PERMANENTLY_REJECTED: "PERMANENTLY_REJECTED",
  // Backend status remains CLOSED (this is named Cancelled in the UI)
  CLOSED: "CLOSED",
  EXPIRED: "EXPIRED",
} as const;

export const SECURITY_CLEARANCE_TASK_DECISION_STATUS = {
  APPROVED: "APPROVED",
  RESUBMIT: "RESUBMIT",
} as const;

export const steps = [
  {
    label: "port-security.step-label1",
    path: portSecurity.personalInfo,
  },
  {
    label: "port-security.step-label2",
    path: portSecurity.socialInfo,
  },
  {
    label: "port-security.step-label3",
    path: portSecurity.occupationInfo,
  },
  {
    label: "port-security.clearance-application",
    path: portSecurity.securityClearanceApplication,
  },
];

const documentsMapper = (documents: any[][]) =>
  documents.flatMap((docs) =>
    (docs ?? [])
      .map((doc: any) => ({
        originalFileName: doc.name,
        url: doc.url ?? doc.documentUrl,
        type: doc.certificateType ?? doc.type,
      }))
      .filter((d) => d.url && d.type),
  );

export const toDateString = (d: Date | string | null | undefined): string => {
  return toDateOnlyString(d);
};

export const mapSecurityClearanceFormDataToPayload = (data: PortSecurityFormData) => {
  // build the relatives array
  const relatives = [] as Relative[];
  if (
    data.spouseName ||
    data.spouseNationality ||
    data.spouseBirthPlace ||
    data.spouseBirthDate ||
    data.spouseOccupation ||
    data.spouseWorkPlace
  ) {
    relatives.push({
      name: data.spouseName,
      type: "SPOUSE",
      nationality: data.spouseNationality,
      placeOfBirth: data.spouseBirthPlace,
      dateOfBirth: toDateString(data.spouseBirthDate),
      occupation: data.spouseOccupation,
      placeOfWork: data.spouseWorkPlace,
      relationship: "SPOUSE",
    });
  }
  if (
    data.fatherName ||
    data.fatherNationality ||
    data.fatherBirthPlace ||
    data.fatherBirthDate ||
    data.fatherOccupation ||
    data.fatherWorkPlace
  ) {
    relatives.push({
      name: data.fatherName,
      type: "FATHER",
      nationality: data.fatherNationality,
      placeOfBirth: data.fatherBirthPlace,
      dateOfBirth: toDateString(data.fatherBirthDate),
      occupation: data.fatherOccupation,
      placeOfWork: data.fatherWorkPlace,
      relationship: "FATHER",
    });
  }
  if (
    data.motherName ||
    data.motherNationality ||
    data.motherBirthPlace ||
    data.motherBirthDate ||
    data.motherOccupation ||
    data.motherWorkPlace
  ) {
    relatives.push({
      name: data.motherName,
      type: "MOTHER",
      nationality: data.motherNationality,
      placeOfBirth: data.motherBirthPlace,
      dateOfBirth: toDateString(data.motherBirthDate),
      occupation: data.motherOccupation,
      placeOfWork: data.motherWorkPlace,
      relationship: "MOTHER",
    });
  }
  data.bloodRelations?.forEach((r) => {
    if (r.name || r.nationality || r.workPlace || r.relation) {
      relatives.push({
        name: r.name,
        type: "BLOOD_RELATIVE",
        nationality: r.nationality,
        placeOfWork: r.workPlace,
        relationship: r.relation,
      });
    }
  });
  data.friends?.forEach((f) => {
    if (f.name || f.nationality || f.workPlace) {
      relatives.push({
        name: f.name,
        type: "FRIEND",
        nationality: f.nationality,
        placeOfWork: f.workPlace,
        relationship: "FRIEND",
      });
    }
  });

  // Ensure we return a string[] (no undefined elements) so payload types match the API contract.
  // Use a map+filter to coerce any non-string/undefined form values to strings and remove empty entries.
  const childrenNames: string[] | undefined = data.children
    ?.map((c) => (c ?? "").toString().trim())
    .filter((c) => c.length > 0);
  const countriesWorkedIn = data.countriesWorkedIn?.length ? data.countriesWorkedIn : undefined;
  const visitedCountries = data.countriesVisited?.length ? data.countriesVisited : undefined;
  const previousOccupations = data.prevOccupations?.length ? data.prevOccupations : undefined;
  const militaryService: MilitaryService = data.hasServed
    ? {
        hasServed: data.hasServed,
        country: data.countryName || undefined,
        typeOfService: data.serviceType || undefined,
        rank: data.rank || undefined,
        duration: data.durationOfService || undefined,
        startDate: toDateString(data.militaryServiceStartDate) || undefined,
        endDate: toDateString(data.militaryServiceEndDate) || undefined,
      }
    : {
        hasServed: data.hasServed,
      };

  const documents = documentsMapper([
    data.passportFront || [],
    data.passportBack || [],
    data.emiratesIdFront || [],
    data.emiratesIdBack || [],
    data.visa || [],
    data.photograph || [],
  ]);
  return {
    applicant: {
      fullNameEnglish: data.fullNameEn,
      fullNameArabic: data.fullNameAr,
      surname: data.surname,
      alias: data.alias || undefined,
      placeOfBirth: data.placeOfBirth,
      dateOfBirth: toDateString(data.dateOfBirth),
      sect: data.sect || undefined,
      religion: data.religion || undefined,
      pastNationality: data.pastNationality || undefined,
      presentNationality: data.presentNationality,
      presentNationalityArabic: data.presentNationalityArabic,
      relatives: relatives.length ? relatives : undefined,
      childrenNames: childrenNames && childrenNames.length ? childrenNames : undefined,
      occupationEnglish: data.occupation,
      occupationArabic: data.occupationAr,

      // occupationEnglish missing from design
      // occupationArabic

      passportNumber: data.passportNumber || undefined,
      passportPlaceOfIssue: data.passportPlaceOfIssue || undefined,
      passportDateOfIssue: toDateString(data.passportDateOfIssue) || undefined,
      passportExpiryDate: toDateString(data.passportExpiryDate) || undefined,

      emiratesIdNumber: data.emiratesIdNumber || undefined,
      emiratesIdPlaceOfIssue: data.emiratesIdPlaceOfIssue || undefined,
      emiratesIdDateOfIssue: toDateString(data.emiratesIdDateOfIssue) || undefined,
      emiratesIdExpiryDate: toDateString(data.emiratesIdExpiryDate) || undefined,

      mobileNumber: data.mobileNumber || undefined,
      emailAddress: data.emailAddress || undefined,
      countriesWorkedIn: countriesWorkedIn || undefined,
      visitedCountries: visitedCountries || undefined,
      previousOccupations: previousOccupations || undefined,
    },
    academicQualifications: {
      qualification: data.academicQualification,
      placeOfStudy: data.placeOfStudy,
      school: data.university,
      languagesKnown: data.languagesKnown || "",
      certificateDate: toDateString(data.certificateDate),
    },
    currentSponsor: {
      name: data.currentSponsor,
      occupation: data.occupation,
      occupationArabic: data.occupationAr,
      presentWorkplace: data.presentWorkplace,
      officeFax: data.officeFax || undefined,
      officeTelephone: data.officeTelephone || undefined,
      bank: data.bank || undefined,
      salary: data.salary || undefined,
    },
    militaryService,
    vehicleDetails: {
      type: data.typeOfVehicle || undefined,
      drivingLicenseNumber: data.licenseNumber || undefined,
      placeOfIssue: data.placeOfIssue || undefined,
      licensedBy: data.licensedBy || undefined,
      issueDate: toDateString(data.dateOfIssue) || undefined,
      expiryDate: toDateString(data.dateOfLicenseExpiry) || undefined,
      plateCode: data.plateCode || undefined,
      plateNo: data.plateNumber || undefined,
    },
    sponsorOfEntry: data.sponsorUponEntry || undefined,
    placeOfEntry: data.placeOfEntry,
    dateOfEntry: toDateString(data.dateOfEntry),
    validityPeriod: validityPeriodStringToInt(data.securityClearanceValidity),
    fottEntry: data.FOTTEntry,
    entryPurposeEnglish: data.entryPurposeEnglish,
    entryPurposeArabic: data.entryPurposeArabic,
    visitorCompanyNameEnglish: data.visitorCompanyEnglish || undefined,
    visitorCompanyNameArabic: data.visitorCompanyArabic || undefined,
    requestingSponsorEnglish: data.sponsorCompanyEnglish || undefined,
    requestingSponsorArabic: data.sponsorCompanyArabic || undefined,
    residency: {
      zone: data.zone || undefined,
      ownerName: data.ownerName || undefined,
      phoneNumber: data.phoneNo || undefined,
      street: data.street || undefined,
      mobileNumber: data.mobileNo || undefined,
      buildingNumber: data.buildingNo || undefined,
      pagerNumber: data.pagerNo || undefined,
      flatNumber: data.flatNo || undefined,
      poBoxNumber: data.poBoxNo || undefined,
      floorNumber: data.floorNo || undefined,
    },
    residenceStatus: {
      residenceOrVisaNumber: data.residenceVisaNo || undefined,
      visaType: data.visaType || undefined,
      dateOfIssue: toDateString(data.visaDateOfIssue) || undefined,
      expiryDate: toDateString(data.visaExpiryDate) || undefined,
      placeOfIssueCode: data.visaPlaceOfIssue || undefined,
    },
    uidNumber: data.uidNumber || undefined,
    requestingSponsorEmailAddress: data.requestingSponsorEmailAddress || undefined,
    signature: {
      signerName: data.signatureName || "",
      signatureDate: toDateString(data.signatureDate),
      signatureAgreement: data.signatureAgreement,
    },
    documents: documents,
    gdprAccepted: data.gdpr,
  };
};

// Map API response to form shape. Returns the mapped data (before cleaning).
export function mapSecurityClearanceData(data: any, t: TFunction): PortSecurityFormData {
  const spouse = data.applicant?.relatives?.find(
    (rel: Relative) =>
      rel.type === "SPOUSE" ||
      rel.relationship?.toLowerCase() === "spouse" ||
      rel.relationship?.toLowerCase() === "husband",
  );
  const father = data.applicant?.relatives?.find(
    (rel: Relative) => rel.relationship?.toLowerCase() === "father",
  );
  const mother = data.applicant?.relatives?.find(
    (rel: Relative) => rel.relationship?.toLowerCase() === "mother",
  );
  // childrenNames may be an array (new backend) or a comma string (legacy)
  const rawChildrenNames = data.applicant?.childrenNames;
  const children = Array.isArray(rawChildrenNames)
    ? rawChildrenNames.map((n: string) =>
        typeof n === "string" ? n.trim() : String(n ?? "").trim(),
      )
    : typeof rawChildrenNames === "string"
      ? rawChildrenNames.split(",").map((name: string) => name.trim())
      : undefined;

  const mappedData: any = {
    // Bio Data
    fullNameEn: data.applicant?.fullNameEnglish,
    fullNameAr: data.applicant?.fullNameArabic,
    surname: data.applicant?.surname,
    alias: data.applicant?.alias,
    placeOfBirth: data.applicant?.placeOfBirth,

    dateOfBirth: data.applicant?.dateOfBirth,
    pastNationality: data.applicant?.pastNationality,
    presentNationality: data.applicant?.presentNationality,
    sect: data.applicant?.sect,
    religion: data.applicant?.religion,
    placeOfEntry: data.placeOfEntry,
    dateOfEntry: data.dateOfEntry,
    sponsorUponEntry: data.sponsorOfEntry,
    emailAddress: data.applicant?.emailAddress,
    mobileNumber: data.applicant?.mobileNumber,

    // Current Sponsor Data
    currentSponsor: data.currentSponsor?.name,
    occupation: data.currentSponsor?.occupation,
    occupationAr: data.currentSponsor?.occupationArabic,
    officeFax: data.currentSponsor?.officeFax,
    presentWorkplace: data.currentSponsor?.presentWorkplace,
    bank: data.currentSponsor?.bank,
    officeTelephone: data.currentSponsor?.officeTelephone,
    salary: data.currentSponsor?.salary,

    // Particulars of Passport
    passportPlaceOfIssue: data.applicant?.passportPlaceOfIssue,
    passportNumber: data.applicant?.passportNumber,
    passportExpiryDate: data.applicant?.passportExpiryDate,
    passportDateOfIssue: data.applicant?.passportDateOfIssue,

    emiratesIdNumber: data.applicant?.emiratesIdNumber,
    emiratesIdPlaceOfIssue: data.applicant?.emiratesIdPlaceOfIssue,
    emiratesIdDateOfIssue: data.applicant?.emiratesIdDateOfIssue,
    emiratesIdExpiryDate: data.applicant?.emiratesIdExpiryDate,

    // Residence / VISA
    visaPlaceOfIssue: data.residenceStatus?.placeOfIssueCode,
    residenceVisaNo: data.residenceStatus?.residenceOrVisaNumber,
    visaType: data.residenceStatus?.visaType,
    visaDateOfIssue: data.residenceStatus?.dateOfIssue,
    visaExpiryDate: data.residenceStatus?.expiryDate,

    // Academic Qualifications
    academicQualification: data.academicQualifications?.qualification,
    placeOfStudy: data.academicQualifications?.placeOfStudy,
    university: data.academicQualifications?.school,
    languagesKnown: data.academicQualifications?.languagesKnown,
    certificateDate: data.academicQualifications?.certificateDate,

    // Social Status
    spouseName: spouse?.name,
    spouseNationality: spouse?.nationality,
    spouseBirthPlace: spouse?.placeOfBirth,
    spouseBirthDate: spouse?.dateOfBirth,
    spouseOccupation: spouse?.occupation,
    spouseWorkPlace: spouse?.placeOfWork,

    // Children
    children,

    // Father
    fatherName: father?.name,
    fatherNationality: father?.nationality,
    fatherBirthPlace: father?.placeOfBirth,
    fatherBirthDate: father?.dateOfBirth,
    fatherWorkPlace: father?.placeOfWork,
    fatherOccupation: father?.occupation,

    // Mother
    motherName: mother?.name,
    motherNationality: mother?.nationality,
    motherBirthPlace: mother?.placeOfBirth,
    motherBirthDate: mother?.dateOfBirth,
    motherWorkPlace: mother?.placeOfWork,
    motherOccupation: mother?.occupation,

    // Other Blood Relations
    bloodRelations: data.applicant?.relatives
      ?.filter((item: Relative) => item.type === "BLOOD_RELATIVE")
      .map((item: Relative) => ({
        name: item.name,
        relation: item.relationship,
        nationality: item.nationality,
        workPlace: item.placeOfWork,
      })),

    // Friends
    friends: data.applicant?.relatives
      ?.filter((item: Relative) => item.type === "FRIEND")
      .map((item: Relative) => ({
        name: item.name,
        nationality: item.nationality,
        workPlace: item.placeOfWork,
      })),

    // Residence
    zone: data.residency?.zone,
    ownerName: data.residency?.ownerName,
    phoneNo: data.residency?.phoneNumber,
    street: data.residency?.street,
    mobileNo: data.residency?.mobileNumber,
    buildingNo: data.residency?.buildingNumber,
    pagerNo: data.residency?.pagerNumber,
    flatNo: data.residency?.flatNumber,
    poBoxNo: data.residency?.poBoxNumber,
    floorNo: data.residency?.floorNumber,

    // Previous occupations
    prevOccupations: data.applicant?.previousOccupations?.map((name: string) => name),

    //Countries Worked In
    countriesWorkedIn: data.applicant?.countriesWorkedIn?.map((name: string) => name),

    // Countries Visited
    countriesVisited: data.applicant?.visitedCountries?.map((name: string) => name),

    //Particulars of Vehicle
    typeOfVehicle: data.vehicleDetails?.type,
    licenseNumber: data.vehicleDetails?.drivingLicenseNumber,
    placeOfIssue: data.vehicleDetails?.placeOfIssue,
    licensedBy: data.vehicleDetails?.licensedBy,
    dateOfIssue: data.vehicleDetails?.issueDate,
    plateNumber: data.vehicleDetails?.plateNo,
    dateOfLicenseExpiry: data.vehicleDetails?.expiryDate,
    plateCode: data.vehicleDetails?.plateCode,

    //Military Field
    hasServed: data.militaryService?.hasServed,
    countryName: data.militaryService?.country,
    serviceType: data.militaryService?.typeOfService,
    rank: data.militaryService?.rank,
    durationOfService: data.militaryService?.duration,
    militaryServiceStartDate: data.militaryService?.startDate,
    militaryServiceEndDate: data.militaryService?.endDate,

    //Signature
    signatureAgreement: data.signature?.signatureAgreement,
    signatureName: data.signature?.signerName,
    signatureDate: data.signature?.signatureDate,

    // Visit Details and Documents
    photograph: data.documents
      ?.filter((doc: Document) => doc.type === "PHOTOGRAPH")
      .map((doc: Document) => ({
        name: doc.originalFileName ?? getFileTitle(doc.url, doc.type),
        type: doc.type,
        url: doc.url,
      })),
    passportFront: data.documents
      ?.filter((doc: any) => doc.type === "PASSPORT_FRONT")
      .map((doc: Document) => ({
        name: doc.originalFileName ?? getFileTitle(doc.url, doc.type),
        type: doc.type,
        url: doc.url,
      })),
    passportBack: data.documents
      ?.filter((doc: any) => doc.type === "PASSPORT_BACK")
      .map((doc: Document) => ({
        name: doc.originalFileName ?? getFileTitle(doc.url, doc.type),
        type: doc.type,
        url: doc.url,
      })),
    emiratesIdFront: data.documents
      ?.filter((doc: any) => doc.type === "EMIRATES_ID_FRONT")
      .map((doc: Document) => ({
        name: doc.originalFileName ?? getFileTitle(doc.url, doc.type),
        type: doc.type,
        url: doc.url,
      })),
    emiratesIdBack: data.documents
      ?.filter((doc: any) => doc.type === "EMIRATES_ID_BACK")
      .map((doc: Document) => ({
        name: doc.originalFileName ?? getFileTitle(doc.url, doc.type),
        type: doc.type,
        url: doc.url,
      })),
    visa: data.documents
      ?.filter((doc: any) => doc.type === "VISA" || doc.certificateType === "VISA")
      .map((doc: Document) => ({
        name: doc.originalFileName ?? getFileTitle(doc.url, doc.type),
        type: doc.type,
        url: doc.url,
      })),
    // FOTT Entry
    FOTTEntry: data.fottEntry,

    // Visit Details
    entryPurposeEnglish: data.entryPurposeEnglish,
    entryPurposeArabic: data.entryPurposeArabic,
    requestingSponsorEmailAddress: data.requestingSponsorEmailAddress,
    securityClearanceValidity: Number.isFinite(Number(data.validityPeriod))
      ? validityPeriodIntToString(Number(data.validityPeriod), t)
      : "",
    sponsorCompanyEnglish: data.requestingSponsorEnglish,
    sponsorCompanyArabic: data.requestingSponsorArabic,
    visitorCompanyEnglish: data.visitorCompanyNameEnglish,
    visitorCompanyArabic: data.visitorCompanyNameArabic,
    uidNumber: data.uidNumber,
    gdpr: data.gdprAccepted,
    presentNationalityArabic: data.applicant?.presentNationalityArabic,
  };

  return mappedData as PortSecurityFormData;
}

type Option = { value: string; label: string };

export const getClearancesFilterFields = (
  t: TFunction,
  nationalityOptions: Option[],
  validityPeriodOptions: Option[],
): Record<string, FilterFieldDefinition> => ({
  passportNumber: {
    label: t("security-clearance.passport-no"),
    type: "text",
    minLength: 3,
    maxLength: 10,
  },
  nationality: {
    label: t("security-clearance.nationality"),
    type: "autocomplete",
    options: nationalityOptions,
  },
  name: {
    label: t("security-clearance.name"),
    type: "text",
    minLength: 3,
  },
  emiratesIdNumber: {
    label: t("security-clearance.emirates-id"),
    type: "text",
    minLength: 3,
    maxLength: 18,
  },
  dateSubmitted: {
    label: t("security-clearance.date-submitted"),
    type: "dateFromToWithoutTime",
    dateConstraint: "not-future",
  },
  validityDate: {
    label: t("security-clearance.validity-period"),
    type: "select",
    options: validityPeriodOptions,
  },
  clearanceStatus: {
    label: t("security-clearance.status"),
    type: "autocomplete",
    options: [
      "PENDING_SPONSOR_COMPANY_APPROVAL",
      "PENDING_SECURITY_DEPT_APPROVAL",
      "PENDING_SECURITY_SUPERVISOR_APPROVAL",
      "PENDING_RESUBMISSION",
      "APPROVED",
      "SUSPENDED",
      "PERMANENTLY_REJECTED",
      "RENEWED",
      // Backend status remains CLOSED (this is named Cancelled in the UI)
      "CLOSED",
      "EXPIRED",
    ].map((code) => ({
      value: code,
      label: t(`security-clearance.status-labels.${code}`),
    })),
  },
});

export function getSecurityClearanceBadgeType(
  status: string | undefined,
): "success" | "warning" | "error" {
  if (!status) return "warning";

  const map: Record<string, "success" | "warning" | "error"> = {
    APPROVED: "success",
    CLOSED: "success",
    RENEWED: "success",

    PENDING_SPONSOR_COMPANY_APPROVAL: "warning",
    PENDING_SECURITY_DEPT_APPROVAL: "warning",
    PENDING_SECURITY_SUPERVISOR_APPROVAL: "warning",
    PENDING_RESUBMISSION: "warning",

    SUSPENDED: "error",
    PERMANENTLY_REJECTED: "error",
    EXPIRED: "error",
  };

  return map[status] ?? "warning";
}

export const validityPeriodStringToInt = (period: string): number => {
  const mapping: Record<string, number> = {
    "7 days": 7,
    "1 month": 30,
    "2 months": 60,
    "3 months": 90,
    "6 months": 180,
    "1 year": 365,
  };
  return mapping[period] ?? 0;
};

export const validityPeriodIntToString = (days: number, t: TFunction): string => {
  const mapping: Record<number, string> = {
    7: t("common.7-days"),
    30: t("common.1-month"),
    60: t("common.2-months"),
    90: t("common.3-months"),
    180: t("common.6-months"),
    365: t("common.1-year"),
  };
  return mapping[days] ?? t("common.x-days", { count: days });
};

export function replaceNullsWithEmptyString(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(replaceNullsWithEmptyString);
  }
  if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, replaceNullsWithEmptyString(v)]),
    );
  }
  return obj === null ? "" : obj;
}

const monthsToDays = (months: number): number => months * 30;

// Extract maximum validity period cap from restricted nationalities.
// Returns 180 (6 months) if no restricted nationalities or all have type missing.
const getMaxRestrictedValidityDays = (restrictedNationalities: readonly Nationality[]): number => {
  if (!restrictedNationalities.length) return 180;
  const maxMonths = Math.max(
    ...restrictedNationalities.map((n) => Number(n.type) || 0).filter((m) => m > 0),
  );
  return maxMonths > 0 ? monthsToDays(maxMonths) : 180;
};

const normalizeNationalityIdentifier = (value: string | null | undefined): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const getRestrictedNationalityIdentifiers = (
  restrictedNationalities: readonly Nationality[],
): Set<string> =>
  new Set(
    restrictedNationalities
      .flatMap((nationality) => [
        nationality.nationality,
        nationality.nationalityArabic,
        nationality.countryCode,
      ])
      .map(normalizeNationalityIdentifier)
      .filter(Boolean),
  );

const mapValidityPeriodOptions = (
  periods: ValidityPeriodType[],
): { label: string; value: string }[] =>
  periods.map((validityPeriod: ValidityPeriodType) => {
    const key = Object.keys(validityPeriod)[0];
    const value = String(validityPeriod[key] ?? "");
    return { label: value, value };
  });

const getRestrictedValidityPeriods = (
  periods: ValidityPeriodType[],
  restrictedNationalities: readonly Nationality[],
): ValidityPeriodType[] => {
  const maxDays = getMaxRestrictedValidityDays(restrictedNationalities);
  return periods.filter((validityPeriod) => {
    const key = Object.keys(validityPeriod)[0];
    const value = String(validityPeriod[key] ?? "");
    return validityPeriodStringToInt(value) <= maxDays;
  });
};

export const isRestrictedNationality = (
  nationality: string | null | undefined,
  restrictedNationalities: readonly Nationality[] = [],
): boolean =>
  getRestrictedNationalityIdentifiers(restrictedNationalities).has(
    normalizeNationalityIdentifier(nationality),
  );

export const getValidityPeriodsOptions = (
  periods: ValidityPeriodType[],
  presentNationality: string | null | undefined,
  restrictedNationalities: readonly Nationality[] = [],
): { label: string; value: string }[] =>
  mapValidityPeriodOptions(
    isRestrictedNationality(presentNationality, restrictedNationalities)
      ? getRestrictedValidityPeriods(periods, restrictedNationalities)
      : periods,
  );

// Bulk approve: if any selected clearance has a restricted nationality, apply that restriction.
export const getBulkValidityPeriodsOptions = (
  periods: ValidityPeriodType[],
  nationalities: readonly string[],
  restrictedNationalities: readonly Nationality[] = [],
): { label: string; value: string }[] => {
  const hasRestrictedNationality = nationalities.some((nationality) =>
    isRestrictedNationality(nationality, restrictedNationalities),
  );
  return mapValidityPeriodOptions(
    hasRestrictedNationality
      ? getRestrictedValidityPeriods(periods, restrictedNationalities)
      : periods,
  );
};

/** Format a raw Emirates ID string (digits or mixed) to the standard dashed display. */
export const formatEmiratesIdNumberValue = (raw: string | null | undefined): string => {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, 15);
  if (!digits) return "";
  let formatted = digits;
  if (digits.length > 3) {
    formatted = digits.slice(0, 3) + "-" + digits.slice(3);
  }
  if (digits.length > 7) {
    formatted = formatted.slice(0, 8) + "-" + formatted.slice(8);
  }
  if (digits.length > 14) {
    formatted = formatted.slice(0, 16) + "-" + formatted.slice(16);
  }
  return formatted;
};

export const formatOnChangeEmiratesIDNumber = (
  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
) => {
  const { value } = event.target;
  event.target.value = formatEmiratesIdNumberValue(value);
};

export function getFileTitle(url: string | undefined, type: string | undefined): string {
  if (url) {
    // Decode URI components and get the last segment
    const decodedUrl = decodeURIComponent(url);
    const filename = decodedUrl.split("/").pop();
    if (filename) return filename;
  }
  return type || "";
}

export function resolveDocumentName({
  name,
  originalFileName,
  url,
  type,
  fallback,
}: {
  name?: string | null;
  originalFileName?: string | null;
  url?: string | null;
  type?: string | null;
  fallback?: string;
}): string {
  const normalizedName = typeof name === "string" ? name.trim() : "";
  if (normalizedName) return normalizedName;

  const normalizedOriginal = typeof originalFileName === "string" ? originalFileName.trim() : "";
  if (normalizedOriginal) return normalizedOriginal;

  if (typeof url === "string" && url.trim()) {
    const resolvedFromUrl = getFileTitle(url, type ?? undefined);
    if (resolvedFromUrl) return resolvedFromUrl;
  }

  if (typeof fallback === "string" && fallback.trim()) {
    return fallback.trim();
  }

  return typeof type === "string" ? type : "";
}

export type CountryLike = {
  countryName?: string | null;
  countryCode?: string | number | null;
};

type CountryOptionValueMode = "code" | "name";

export const syncDuplicateCountryErrors = (
  values: string[],
  fieldName: keyof Pick<PortSecurityFormData, "countriesVisited" | "countriesWorkedIn">,
  setError: UseFormSetError<PortSecurityFormData>,
  clearErrors: UseFormClearErrors<PortSecurityFormData>,
  t: TFunction,
) => {
  clearErrors(fieldName);

  const firstIndexByValue = new Map<string, number>();

  values.forEach((value, index) => {
    const normalizedValue = String(value ?? "")
      .trim()
      .toLowerCase();
    if (!normalizedValue) return;

    const firstIndex = firstIndexByValue.get(normalizedValue);
    if (firstIndex === undefined) {
      firstIndexByValue.set(normalizedValue, index);
      return;
    }

    setError(`${fieldName}.${firstIndex}` as any, {
      type: "manual",
      message: t("security-clearance.country-duplicate"),
    });
    setError(`${fieldName}.${index}` as any, {
      type: "manual",
      message: t("security-clearance.country-duplicate"),
    });
  });
};

export function buildCountryOptions(
  countries: readonly CountryLike[] | null | undefined,
  {
    valueMode = "code",
    formatName,
  }: {
    valueMode?: CountryOptionValueMode;
    formatName?: (name: string) => string;
  } = {},
): Array<{ label: string; value: string }> {
  const valid = (countries ?? []).filter(
    (country): country is { countryName: string; countryCode: string | number } =>
      !!(country?.countryName && country?.countryCode),
  );

  const options = valid.map((country) => {
    const code = String(country.countryCode).toUpperCase();
    const name = formatName ? formatName(country.countryName) : country.countryName;

    return {
      label: `${name} (${code})`,
      value: valueMode === "name" ? name : code,
    };
  });

  options.sort((a, b) => a.label.localeCompare(b.label));
  return options;
}

// Returns the entire tasksHistory sorted descending by createdOn.
export function getSortedTaskHistory(
  tasksHistory: Array<SecurityClearanceTaskHistoryEntry> = [],
): Array<SecurityClearanceTaskHistoryEntry> {
  if (!Array.isArray(tasksHistory) || tasksHistory.length === 0) return [];
  return tasksHistory
    .filter((t) => t?.createdOn)
    .sort((a, b) => new Date(b.createdOn!).getTime() - new Date(a.createdOn!).getTime());
}

// Given a tasks history already sorted descending by createdOn (see getSortedTaskHistory)
export function getLatestTaskWithReason(
  sortedTasksHistory: Array<SecurityClearanceTaskHistoryEntry> = [],
) {
  if (!Array.isArray(sortedTasksHistory) || sortedTasksHistory.length === 0) return undefined;
  return sortedTasksHistory.find(
    (t) => typeof t?.taskDecision?.reason === "string" && t.taskDecision!.reason.trim().length > 0,
  );
}

// Parse API date strings for ordering; invalid/missing and sort by last
export function securityClearanceReasonTimeMs(iso?: string | null): number {
  if (!iso || typeof iso !== "string") return Number.NEGATIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
}

function taskHistoryDecisionTimeMs(task: SecurityClearanceTaskHistoryEntry): number {
  const td = task.taskDecision;
  const tDecision = securityClearanceReasonTimeMs(td?.timestamp);
  if (tDecision > Number.NEGATIVE_INFINITY) return tDecision;
  const tModified = securityClearanceReasonTimeMs(task.modifiedOn);
  if (tModified > Number.NEGATIVE_INFINITY) return tModified;
  return securityClearanceReasonTimeMs(task.createdOn);
}

// Tie-break when two events share the same millisecond. Higher wins.
function sourcePriority(source: SecurityClearanceLatestReasonSource): number {
  switch (source) {
    case "permanentRejection":
      return 3;
    case "suspension":
      return 2;
    case "tasksHistory":
      return 1;
    default:
      return 0;
  }
}

// True when the chronologically latest decision has something to show in View details (notes and/or task attachments).
export function securityClearanceDecisionHasViewableDetails(
  event: SecurityClearanceLatestReasonEvent | undefined,
): boolean {
  if (!event) return false;
  if (event.source === "tasksHistory") {
    const reason = (event.reason ?? "").trim();
    const raw = event.tasksHistoryEntry?.taskDecision?.attachments;
    const hasAttachments = Array.isArray(raw) && raw.length > 0;
    return reason.length > 0 || hasAttachments;
  }
  return (event.reason ?? "").trim().length > 0;
}

// Picks the single most recent *decision* across tasksHistory (every row with a taskDecision),
// suspension, and permanent rejection, using decision/event timestamps (not request status).
export function getLatestSecurityClearanceDecisionEvent(params: {
  tasksHistory?: SecurityClearanceTaskHistoryEntry[] | null;
  suspension?: { reason?: string | null; suspendedAt?: string | null } | null;
  permanentRejection?: { reason?: string | null; rejectedAt?: string | null } | null;
}): SecurityClearanceLatestReasonEvent | undefined {
  const candidates: SecurityClearanceLatestReasonEvent[] = [];

  const tasks = Array.isArray(params.tasksHistory) ? params.tasksHistory : [];
  for (const task of tasks) {
    if (task?.taskDecision == null || typeof task.taskDecision !== "object") continue;
    const raw = task.taskDecision.reason;
    const reason = typeof raw === "string" ? raw.trim() : "";
    const occurredAtMs = taskHistoryDecisionTimeMs(task);
    candidates.push({
      source: "tasksHistory",
      occurredAtMs,
      reason,
      tasksHistoryEntry: task,
    });
  }

  const susp = params.suspension;
  if (susp && typeof susp === "object") {
    const reason = typeof susp.reason === "string" ? susp.reason.trim() : "";
    const occurredAtMs = securityClearanceReasonTimeMs(susp.suspendedAt);
    candidates.push({
      source: "suspension",
      occurredAtMs,
      reason,
    });
  }

  const pr = params.permanentRejection;
  if (pr && typeof pr === "object") {
    const reason = typeof pr.reason === "string" ? pr.reason.trim() : "";
    const occurredAtMs = securityClearanceReasonTimeMs(pr.rejectedAt);
    candidates.push({
      source: "permanentRejection",
      occurredAtMs,
      reason,
    });
  }

  if (candidates.length === 0) return undefined;

  candidates.sort((a, b) => {
    if (b.occurredAtMs !== a.occurredAtMs) return b.occurredAtMs - a.occurredAtMs;
    const prDiff = sourcePriority(b.source) - sourcePriority(a.source);
    if (prDiff !== 0) return prDiff;
    if (a.source === "tasksHistory" && b.source === "tasksHistory") {
      const cb = securityClearanceReasonTimeMs(b.tasksHistoryEntry?.createdOn);
      const ca = securityClearanceReasonTimeMs(a.tasksHistoryEntry?.createdOn);
      return cb - ca;
    }
    return 0;
  });

  return candidates[0];
}

type SecurityClearanceDisplayStatusParams = {
  apiStatus: string;
  fakeStatus?: string;
  taskStatus?: string | null;
  tasksHistory?: SecurityClearanceTaskHistoryEntry[] | null;
};

// Resolves the status shown in preview when the API status lags behind workflow progress.
export function resolveSecurityClearanceDisplayStatus({
  apiStatus,
  fakeStatus = "",
  taskStatus = null,
  tasksHistory = null,
}: SecurityClearanceDisplayStatusParams): string {
  if (fakeStatus) {
    return fakeStatus;
  }

  if (apiStatus !== SECURITY_CLEARANCE_STATUS.PENDING_RESUBMISSION) {
    return apiStatus;
  }

  const isSecurityTaskActive = taskStatus === "READY" || taskStatus === "CLAIMED";
  if (!isSecurityTaskActive) {
    return apiStatus;
  }

  const latestEvent = getLatestSecurityClearanceDecisionEvent({ tasksHistory });
  const latestDecision = latestEvent?.tasksHistoryEntry?.taskDecision;
  const latestDecisionStatus = latestDecision?.status;
  const latestDecisionRole = latestDecision?.submittedByRole;

  if (latestDecisionStatus === SECURITY_CLEARANCE_TASK_DECISION_STATUS.RESUBMIT) {
    return apiStatus;
  }

  if (latestDecisionStatus === SECURITY_CLEARANCE_TASK_DECISION_STATUS.APPROVED) {
    if (latestDecisionRole === "security-operator") {
      return SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_SUPERVISOR_APPROVAL;
    }
    if (latestDecisionRole === "security-supervisor") {
      return SECURITY_CLEARANCE_STATUS.APPROVED;
    }
    return SECURITY_CLEARANCE_STATUS.PENDING_SECURITY_DEPT_APPROVAL;
  }

  return apiStatus;
}

// Format ISO date string to "DD/MM/YYYY HH:mm"
export function formatDateFull(dateString: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export function extractVerificationDetails(data: SecurityClearanceDetails) {
  const sortedTasksHistory = getSortedTaskHistory(data?.tasksHistory);

  const isAnyTaskVerifiedBySecurityOperator = sortedTasksHistory.find(
    (task) =>
      task.taskDecision?.status === SECURITY_CLEARANCE_TASK_DECISION_STATUS.APPROVED &&
      task.taskDecision?.submittedByRole === SECURITY_OPERATOR_ROLE,
  );
  // is Security Clearance submitted by Security Operator or Security Supervisor,
  // without specifying a Sponsor Company
  const isSubmittedWithNoSponsorFallback =
    data.requestingSponsorEnglish === null &&
    (data.submittedByRole === SECURITY_OPERATOR_ROLE ||
      data.submittedByRole === SECURITY_SUPERVISOR_ROLE);

  // If any task is verified by a Security Operator, return the submittedBy and timestamp from that taskDecision.
  if (isAnyTaskVerifiedBySecurityOperator) {
    return {
      submittedBy: isAnyTaskVerifiedBySecurityOperator.taskDecision?.submittedBy,
      timestamp: isAnyTaskVerifiedBySecurityOperator.taskDecision?.timestamp,
    };
  }
  // If the Security Clearance is submitted by a Security Operator or Security Supervisor without specifying a Sponsor Company, return the submittedBy and timestamp from the last resubmission task if the status is PENDING_RESUBMISSION, otherwise return the submittedBy and timestamp from the main task.
  if (isSubmittedWithNoSponsorFallback) {
    // if the status is PENDING_RESUBMISSION, return the submittedBy and createdOn from the last resubmission task
    if (data.status === SECURITY_CLEARANCE_STATUS.PENDING_RESUBMISSION) {
      const lastResubmissionTask = sortedTasksHistory.find(
        (task) => task.taskDecision?.status === SECURITY_CLEARANCE_TASK_DECISION_STATUS.RESUBMIT,
      );
      return {
        submittedBy: data.submittedByUsername,
        timestamp: lastResubmissionTask?.createdOn,
      };
    }
    // otherwise, return the creation time of the main task
    return {
      submittedBy: data.submittedByUsername,
      timestamp: data.task?.createdAt,
    };
  }

  return null;
}
